import { useToast } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import {
  getControleFornecedorEscopoDia,
  IControleFornecedorEscopoDiaFiltro,
  IControleFornecedorEscopoDiaItem,
  IEscopoDiaPagination
} from '@service/controlefornecedor'

export interface IEscopoDiaFilters {
  dataInicio: string
  dataFim: string
  empresasIds?: number[]
  lojasIds?: number[]
  fornecedoresIds?: number[]
  categoriasCompactasIds?: number[]
  tipos?: number[]
  compradores?: string[]
}

const DEFAULT_ROWS = 50

export default function useEscopoFornecedor() {
  const toast = useToast()
  const [data, setData] = useState<IControleFornecedorEscopoDiaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<IEscopoDiaPagination | null>(
    null
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<IEscopoDiaFilters>({
    dataInicio: '',
    dataFim: ''
  })

  const validateFilters = useCallback((f: IEscopoDiaFilters): string | null => {
    if (!f.dataInicio || !f.dataFim) {
      return 'Período é obrigatório. Informe data início e data fim.'
    }

    const inicio = new Date(f.dataInicio)
    const fim = new Date(f.dataFim)

    if (fim < inicio) {
      return 'Data final não pode ser anterior à data inicial.'
    }

    const diffDays = Math.ceil(
      (fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (diffDays > 366) {
      return 'O intervalo máximo permitido é de 366 dias.'
    }

    return null
  }, [])

  const fetchData = useCallback(
    async (f: IEscopoDiaFilters, page = 1) => {
      const validationError = validateFilters(f)
      if (validationError) {
        toast({
          title: 'Validação',
          description: validationError,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        return
      }

      setLoading(true)
      setError(null)

      try {
        const filtro: IControleFornecedorEscopoDiaFiltro = {
          dataInicio: f.dataInicio,
          dataFim: f.dataFim,
          empresasIds: f.empresasIds?.length ? f.empresasIds : undefined,
          lojasIds: f.lojasIds?.length ? f.lojasIds : undefined,
          fornecedoresIds: f.fornecedoresIds?.length
            ? f.fornecedoresIds
            : undefined,
          categoriasCompactasIds: f.categoriasCompactasIds?.length
            ? f.categoriasCompactasIds
            : undefined,
          tipos: f.tipos?.length ? f.tipos : undefined,
          compradores: f.compradores?.length ? f.compradores : undefined
        }

        const { data: res } = await getControleFornecedorEscopoDia(filtro, {
          actualPage: page,
          rows: DEFAULT_ROWS
        })

        if (res.success) {
          setData(res.data ?? [])
          setPagination(res.pagination ?? null)
          setCurrentPage(page)
          setFilters(f)
        } else {
          throw new Error(res.message)
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          'Erro ao carregar dados do escopo'
        setError(msg)
        toast({
          title: 'Erro ao carregar escopo',
          description: msg,
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      } finally {
        setLoading(false)
      }
    },
    [validateFilters, toast]
  )

  const goToPage = useCallback(
    (page: number) => {
      fetchData(filters, page)
    },
    [fetchData, filters]
  )

  return {
    data,
    loading,
    error,
    pagination,
    currentPage,
    filters,
    fetchData,
    goToPage,
    validateFilters
  }
}
